import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Menu,
  X,
  ChevronDown,
  LayoutDashboard,
  GraduationCap,
  Users,
  BookOpen,
  type LucideIcon,
} from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import logoImg from "@/assets/logo.webp";

export type NavLink = {
  label: string;
  href: string;
  isMegaMenu?: boolean;
  megaMenuContent?: {
    title: string;
    description: string;
    items: {
      label: string;
      description: string;
      href: string;
      icon: LucideIcon;
    }[];
  };
};

const navLinks: NavLink[] = [
  { label: "Home", href: "/" },
  { label: "Platform", href: "/platform" },
  { label: "Features", href: "/features" },
  {
    label: "Applications",
    href: "#",
    isMegaMenu: true,
    megaMenuContent: {
      title: "Applications",
      description: "Explore eSchool's comprehensive suite. Unleash the potential of our modules to streamline your school operations, workflows, and communication efficiency.",
      items: [
        {
          label: "Admin Dashboard",
          description: "Complete control center for school administrators and staff.",
          href: "/admin-dashboard",
          icon: LayoutDashboard
        },
        {
          label: "Teacher App",
          description: "Empower educators with attendance, grading, and communication tools.",
          href: "/teacher-app",
          icon: GraduationCap
        },
        {
          label: "Parent App",
          description: "Keep parents informed with real-time updates and fee tracking.",
          href: "/parent-app",
          icon: Users
        },
        {
          label: "Student App",
          description: "Provide students access to their timetable, assignments, and results.",
          href: "/student-app",
          icon: BookOpen
        },
      ]
    }
  },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <a
        href="#main-content"
        className="absolute left-4 top-2 z-[60] -translate-y-20 rounded-md bg-background px-4 py-3 font-semibold text-foreground shadow-lg outline-none ring-2 ring-ring ring-offset-2 transition-transform focus:translate-y-0 motion-reduce:transition-none"
      >
        Skip to main content
      </a>
      <Link
        to="/register-school"
        aria-current={location.pathname === "/register-school" ? "page" : undefined}
        className="flex min-h-11 w-full items-center justify-center gap-2 bg-primary px-4 py-2 text-center text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-inset motion-reduce:transition-none"
      >
        <span className="sm:hidden">Registration open for 10 schools.</span>
        <span className="hidden sm:inline">Registration open: We are selecting only 10 schools for the 2019 E.C. Academic Year.</span>
        <span className="shrink-0 font-bold underline underline-offset-2">Apply now</span>
      </Link>
      <nav aria-label="Primary navigation" className={scrolled
        ? "bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-b border-white/20 dark:border-slate-700/30 shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
        : "bg-[hsl(200,100%,97%)]/80 backdrop-blur-sm border-b border-transparent"
        }>
      <div className="container mx-auto grid h-16 grid-cols-[1fr_auto_1fr] items-center px-4 lg:px-8">
        <Link
          to="/"
          aria-label="eSchool home"
          className="flex min-h-11 w-fit items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <img src={logoImg} alt="" width="96" height="40" className="h-10 w-24" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden items-center gap-1 xl:flex">
          {navLinks.map((link) =>
            link.isMegaMenu ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
                onFocus={() => setDropdownOpen(true)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    setDropdownOpen(false);
                  }
                }}
              >
                <button
                  type="button"
                  aria-expanded={dropdownOpen}
                  aria-controls="applications-menu"
                  aria-haspopup="true"
                  onClick={() => setDropdownOpen((open) => !open)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setDropdownOpen(false);
                      event.currentTarget.focus();
                    }
                  }}
                  className="flex min-h-11 items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {link.label}
                  <ChevronDown aria-hidden="true" className={`w-3.5 h-3.5 transition-transform duration-200 motion-reduce:transition-none ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      id="applications-menu"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
                      className="absolute top-full -left-48 mt-2 w-[700px] bg-background rounded-2xl shadow-2xl border p-6 z-50 grid grid-cols-3 gap-8"
                    >
                      <div className="col-span-1 space-y-4 pr-6 border-r">
                        <h3 className="text-xl font-heading font-bold text-foreground">{link.megaMenuContent?.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {link.megaMenuContent?.description}
                        </p>
                        <Link
                          to="/features"
                          className="mt-1 inline-flex min-h-11 items-center gap-1 rounded-md text-sm font-semibold text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          View All Features →
                        </Link>
                      </div>
                      <div className="col-span-2 grid grid-cols-2 gap-4">
                        {link.megaMenuContent?.items.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="group flex min-h-11 flex-col gap-2 rounded-xl p-3 transition-colors hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                <child.icon aria-hidden="true" className="w-5 h-5" />
                              </div>
                              <span className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
                                {child.label}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed pl-[3.25rem]">
                              {child.description}
                            </p>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                aria-current={location.pathname === link.href ? "page" : undefined}
                className={`flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${location.pathname === link.href
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                  }`}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden items-center justify-self-end xl:flex">
          <Button variant="default" size="lg" asChild>
            <Link to="/contact">
              Request a Free Demo
            </Link>
          </Button>
        </div>

        {/* Mobile Actions */}
        <div className="col-start-3 flex items-center justify-self-end gap-2 xl:hidden">
          <Button className="min-h-11 rounded-full px-4 shadow-sm" asChild>
            <Link to="/contact">Contact</Link>
          </Button>
          <button
            type="button"
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation-menu"
            className="flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-foreground hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X aria-hidden="true" className="h-6 w-6" /> : <Menu aria-hidden="true" className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="mobile-navigation-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
            className="overflow-hidden border-t bg-background xl:hidden"
          >
            <div className="p-4 space-y-1">
              {navLinks.map((link) => {
                if (link.isMegaMenu) {
                  return (
                    <div key={link.label}>
                      <button
                        type="button"
                        aria-expanded={dropdownOpen}
                        aria-controls="mobile-applications-menu"
                        onClick={() => setDropdownOpen((open) => !open)}
                        className="flex min-h-11 w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium text-muted-foreground hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        {link.label}
                        <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform motion-reduce:transition-none ${dropdownOpen ? "rotate-180" : ""}`} />
                      </button>
                      {dropdownOpen && (
                        <div id="mobile-applications-menu">
                          {link.megaMenuContent?.items.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="flex min-h-11 items-center gap-3 rounded-lg px-6 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              onClick={() => setMobileOpen(false)}
                            >
                              <child.icon aria-hidden="true" className="w-4 h-4 text-primary" />
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    aria-current={location.pathname === link.href ? "page" : undefined}
                    className="flex min-h-11 items-center rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="pt-3">
                <Button variant="default" className="min-h-11 w-full" asChild>
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>
                    Request a Free Demo
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </nav>
    </div>
  );
};

export default Navbar;
